import React, { useEffect, useRef, useState } from 'react';

import { ChevronRight } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { IconWithFallback } from 'components/IconWithFallback';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useMenuTitle } from 'hooks/useMenuTitleContext';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { useSideBar } from 'hooks/useSideBar';
import { cn } from 'lib/utils';
import { Icon, MenuItem, Feature } from 'types/menuTypes';

interface IconData {
  url: string;
  name?: string;
}

interface IconProps {
  style: {
    width: string;
    height: string;
  };
}

interface IButtonSibBar {
  isActive?: boolean;
  path: string;
  subtitle?: string;
  icon: IconData | IconData[] | React.ComponentType<IconProps> | React.ReactNode;
  subMenu: MenuItem[];
  isSubMenu?: boolean;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  level: 'first' | 'second' | 'third';
  collapsed?: boolean;
  isExpanded?: boolean;
  feature?: Feature | null; // NEW: Added for icon fallback system
}

const ButtonSidebar = ({
  isActive = false,
  path,
  subtitle = '',
  icon,
  subMenu = [],
  isSubMenu = false,
  label,
  onClick,
  disabled = false,
  level,
  collapsed = false,
  isExpanded = false,
  feature,
}: IButtonSibBar) => {
  const { pathname: rawPathname } = useLocation();
  const pathname = decodeURIComponent(rawPathname);
  const navigate = useNavigate();
  const { open } = useSideBar();
  const { updateMenuTitle } = useMenuTitle();
  const {
    setFirstLevelMenu,
    setSecondLevelMenu,
    setThirdLevelMenu,
    selectMenuByDocumentId,
    firstLevelMenu,
    secondLevelMenu,
    thirdLevelMenu,
  } = useMenuSelection();

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(buttonRef, () => {
    setIsSubMenuOpen(false);
  });

  useEffect(() => {
    if (!open) {
      setIsSubMenuOpen(false);
    }
  }, [open]);

  // Recursive function to check if a menu is a descendant of another
  function isDescendant(menu: MenuItem, targetPath: string): boolean {
    if (!menu.subMenu) {
      return false;
    }

    for (const sub of menu.subMenu) {
      if (sub.path === targetPath) {
        return true;
      }

      const recursiveResult = isDescendant(sub, targetPath);
      if (recursiveResult) {
        return true;
      }
    }

    return false;
  }

  // Function to check if a menu is active based on the menu cascade
  const isMenuActive = () => {
    // If it's a first-level menu
    if (level === 'first') {
      if (pathname === path) return true;
      if (subMenu.some(item => item.path === pathname)) return true;
      return subMenu.some(item => item.subMenu?.some(subItem => subItem.path === pathname));
    }

    // If it's a second-level menu
    if (level === 'second') {
      // Check if the second level path matches the pathname
      if (pathname === path) {
        return true;
      }

      // Check if the second level is selected in the context
      if (secondLevelMenu && secondLevelMenu.path === path) {
        return true;
      }

      // Check if the current third level is a descendant of this second level
      if (thirdLevelMenu) {
        const isDesc = isDescendant(
          {
            label,
            path,
            icon: icon as Icon,
            isActive: isActive,
            isMobile: false,
            rank: 0,
            feature: null,
            level,
            subMenu: subMenu,
          },
          thirdLevelMenu.path
        );

        if (isDesc) {
          return true;
        }
      }

      return false;
    }

    // If it's a third-level menu
    if (level === 'third') {
      if (pathname === path) return true;
      if (thirdLevelMenu && thirdLevelMenu.path === path) return true;
      return false;
    }

    return false;
  };

  const isActiveLocation = isMenuActive();

  const handleClick = () => {
    if (disabled) return;

    // Update menu context only if needed
    const menuItem: MenuItem = {
      label,
      path,
      icon: icon as Icon,
      isActive: true,
      isMobile: false,
      rank: 0,
      feature: null,
      level,
      subMenu: subMenu,
    };

    if (onClick) {
      onClick();
      return;
    }

    if (subMenu.length > 0) {
      setIsSubMenuOpen((prev: boolean) => !prev);
      return;
    }

    // Special case: second level without feature.documentId, but with submenus
    if (level === 'second' && !menuItem.feature?.documentId && subMenu.length > 0) {
      const firstChild = subMenu[0];
      if (firstChild?.feature?.documentId) {
        // Always pass the specific path to ensure correct navigation
        selectMenuByDocumentId(firstChild.feature.documentId, true, undefined, firstChild.path);

        return;
      }
    }

    navigate(path);
    updateMenuTitle(label);

    if (level === 'first') {
      setFirstLevelMenu(menuItem);
      setSecondLevelMenu(null);
      setThirdLevelMenu(null);
    } else if ((level === 'second' || level === 'third') && menuItem.feature?.documentId) {
      // Always pass the specific path to ensure correct navigation
      selectMenuByDocumentId(menuItem.feature.documentId, true, undefined, path);
    }
  };

  const renderIcon = () => {
    // If it's a valid React element
    if (React.isValidElement(icon)) {
      return icon;
    }

    // If it's a function component
    if (typeof icon === 'function') {
      const IconComponent = icon as React.ComponentType<IconProps>;
      return <IconComponent style={{ width: '1.5rem', height: '1.5rem' }} />;
    }

    // Extract icon data and URL for all other cases
    let strapiIconUrl: string | null = null;
    let iconAlt: string = label;

    if (Array.isArray(icon)) {
      // If the icon is an array (like Cockpit), take the first icon
      const iconData = icon[0];
      strapiIconUrl = iconData?.url || null;
      iconAlt = iconData?.name || label;
    } else if (typeof icon === 'object' && icon && 'url' in icon) {
      // If it's an object with url (Icon type from Strapi)
      const iconData = icon as IconData;
      strapiIconUrl = iconData.url || null;
      iconAlt = iconData.name || label;
    }
    // else: no icon provided, strapiIconUrl stays null

    // Single IconWithFallback call with 4-level fallback hierarchy
    return (
      <IconWithFallback
        strapiIconUrl={strapiIconUrl}
        featureName={feature?.name}
        fallbackPath={path}
        alt={iconAlt}
        className="w-6 h-6"
      />
    );
  };

  const hasSubMenu = subMenu.length > 0;
  // Show tooltip only when sidebar is collapsed
  const showTooltip = collapsed || !open;

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              // Base styles
              'relative w-full flex items-center',
              'transition-all duration-200 ease-in-out',
              // Height: fixed for first level, auto for submenu (to allow text wrap)
              isSubMenu ? 'min-h-[2.75rem] py-2' : 'min-h-[3.5rem]',
              // Active state with left indicator bar
              isActiveLocation && [
                'bg-primary/10 text-primary',
                'before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2',
                'before:h-6 before:w-1 before:rounded-r-full before:bg-primary',
              ],
              // Hover state (only when not active and not disabled)
              !isActiveLocation && !disabled && 'hover:bg-muted/50',
              // Disabled state
              disabled && 'opacity-50 cursor-not-allowed',
              // Enabled state
              !disabled && 'cursor-pointer',
              // Submenu item styling - indent with more breathing room
              isSubMenu && 'pl-3 pr-2',
              // Focus visible styles for keyboard navigation
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset'
            )}
            onClick={handleClick}
            ref={buttonRef}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={showTooltip ? label : undefined}
            aria-disabled={disabled}
            aria-expanded={hasSubMenu ? isExpanded || isSubMenuOpen : undefined}
            aria-current={isActiveLocation ? 'page' : undefined}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }}
          >
            <div
              className={cn(
                'px-3 py-2 w-full h-full flex items-center gap-3',
                collapsed && 'justify-center'
              )}
            >
              {/* Icon container with consistent sizing */}
              <div
                className={cn(
                  'flex-shrink-0 flex items-center justify-center',
                  'w-6 h-6',
                  isActiveLocation && 'text-primary',
                  disabled && 'opacity-60'
                )}
              >
                {renderIcon()}
              </div>

              {/* Label and subtitle */}
              {!collapsed && open && (
                <div className="flex-1 min-w-0 flex flex-col">
                  <span
                    className={cn(
                      'text-sm leading-tight',
                      // Submenu items: allow text wrap (2 lines max)
                      isSubMenu ? 'line-clamp-2' : 'truncate',
                      isActiveLocation
                        ? 'font-semibold text-primary'
                        : 'font-medium text-foreground',
                      disabled && 'text-muted-foreground'
                    )}
                  >
                    {label}
                  </span>
                  {subtitle && (
                    <span className="text-xs text-muted-foreground truncate">{subtitle}</span>
                  )}
                </div>
              )}

              {/* Chevron indicator for items with submenu */}
              {!collapsed && open && hasSubMenu && (
                <ChevronRight
                  className={cn(
                    'flex-shrink-0 w-4 h-4 transition-transform duration-200',
                    isActiveLocation ? 'text-primary' : 'text-muted-foreground',
                    (isExpanded || isSubMenuOpen) && 'rotate-90'
                  )}
                />
              )}
            </div>
          </div>
        </TooltipTrigger>
        {showTooltip && (
          <TooltipContent side="right" align="center" className="z-[200]">
            <p className="text-sm font-medium">{label}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

export default ButtonSidebar;
